import { Col, Divider, Modal, Row, Typography } from "antd"
import { createRef, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Tip } from "../../../../domain/entities/Tip"
import MainForm, { MainFormRef } from "../../../components/Form/MainForm"
import { FormField } from "../../../components/Form/FormField"
import { InputType } from "../../../components/TextInput/const/InputType.enum"
import { MarketplaceTipItem } from "../MarketplacesViewModel"

interface Props {
	onConfirm?: (tip: MarketplaceTipItem) => void
	onCancel?: () => void
	visible: boolean
	tips: Tip[]
}

const MarketplaceTipModal = ({ visible, tips, onConfirm, onCancel }: Props) => {
	const { t } = useTranslation("marketplaces", { keyPrefix: "tipsInfo" })
	const initialModalData = {
		id: undefined,
		position: 1
	}
	const [modalData, setModalData] = useState<{ id?: number; position: number }>(initialModalData)
	const formRef = createRef<MainFormRef>()

	useEffect(() => {
		setModalData(initialModalData)
	}, [visible])

	return (
		<Modal
			title={
				<div style={{ position: "relative", bottom: 24 }}>
					<Typography.Title level={5}>{t("modalTitle")}</Typography.Title>
					<Divider style={{ marginBottom: 0 }} />
				</div>
			}
			centered
			style={{ overflowY: "auto" }}
			open={visible}
			onOk={async () => {
				await formRef.current?.validateFields()
				const selectedTip = tips.find(item => item.id === modalData.id)
				selectedTip &&
					onConfirm &&
					onConfirm({
						tipId: selectedTip.id,
						position: Number(modalData.position ?? 1),
						tip: selectedTip
					})
			}}
			onCancel={() => onCancel && onCancel()}
			width={600}
			okText={t("confirm")}
			cancelText={t("cancel")}
		>
			<MainForm
				initialValues={modalData}
				hideFormButtons
				key={"marketplaceTipModal"}
				onChangeFormValues={formData => {
					if (JSON.stringify(modalData) !== JSON.stringify(formData)) {
						setModalData({
							...modalData,
							...formData
						})
					}
				}}
				className="flex flex-col justify-center items-center"
				ref={formRef}
			>
				<Row gutter={[24, 0]} className="w-full">
					<Col md={24} xl={24} xs={24}>
						<Row gutter={[24, 0]}>
							<Col xl={22} md={22} xs={22} className="mx-auto">
								<FormField.SelectSearch
									key={"id"}
									label={t("tip")}
									name={"id"}
									mandatory
									value={modalData?.id}
									placeholder={t("tipPlaceholder").toString()}
									onChange={selected => {
										setModalData({
											...modalData,
											id: selected.value
										})
									}}
									options={tips.map(tip => ({
										label: `${tip.id} - ${tip.user?.name || "-"} - ${tip.description || "-"}`,
										value: tip.id,
										title: tip.description
									}))}
									popupMatchSelectWidth={true}
								/>
							</Col>
						</Row>
						<Row gutter={[24, 0]}>
							<Col xl={22} md={22} xs={22} className="mx-auto mb-2">
								<FormField.Input
									inputType={InputType.NUMBER}
									key={"position"}
									label={t("position")}
									name={"position"}
									mandatory
								/>
							</Col>
						</Row>
					</Col>
				</Row>
			</MainForm>
		</Modal>
	)
}

export default MarketplaceTipModal
